import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Save, Trash2, Eye, EyeOff, Upload } from 'lucide-react';
import BenevolCheckLogo from '../components/BenevolCheckLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader, refreshUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    association_name: '',
    association_address: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [currentLogo, setCurrentLogo] = useState(null);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        association_name: user.association_name || '',
        association_address: user.association_address || ''
      }));
      // Charger le logo actuel si l'association en a un
      if (user.logo_path) {
        setCurrentLogo(`${API}/logos/${user.logo_path}`);
      }
    }
  }, [user]);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier la taille (max 2 Mo)
      if (file.size > 2 * 1024 * 1024) {
        setError('Le logo ne doit pas dépasser 2 Mo');
        return;
      }
      // Vérifier le type
      if (!['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'].includes(file.type)) {
        setError('Format non supporté. Utilisez PNG, JPG, GIF ou WebP');
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    
    setUploadingLogo(true);
    setError('');
    
    try {
      const formDataLogo = new FormData();
      formDataLogo.append('file', logoFile);
      
      await axios.post(`${API}/users/me/logo`, formDataLogo, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSuccess('Logo mis à jour avec succès');
      setLogoFile(null);
      setCurrentLogo(logoPreview);
      setLogoPreview(null);
      
      // Rafraîchir les données utilisateur si la fonction existe
      if (refreshUser) {
        refreshUser();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'upload du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validate password change
    if (formData.new_password) {
      if (formData.new_password !== formData.confirm_password) {
        setError('Les nouveaux mots de passe ne correspondent pas');
        setLoading(false);
        return;
      }
      if (!formData.current_password) {
        setError('Veuillez entrer votre mot de passe actuel');
        setLoading(false);
        return;
      }
    }

    try {
      const updateData = {
        name: formData.name,
        email: formData.email
      };

      if (user?.role === 'association_admin') {
        updateData.association_name = formData.association_name;
        updateData.association_address = formData.association_address;
      }

      if (formData.new_password) {
        updateData.current_password = formData.current_password;
        updateData.new_password = formData.new_password;
      }

      await axios.put(`${API}/auth/me`, updateData, {
        headers: getAuthHeader()
      });

      setSuccess('Profil mis à jour avec succès');
      setFormData(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'SUPPRIMER') {
      setError('Veuillez taper SUPPRIMER pour confirmer');
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/auth/me`, {
        headers: getAuthHeader()
      });
      logout();
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression');
      setLoading(false);
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case 'volunteer': return 'Bénévole';
      case 'association_admin': return 'Responsable associatif';
      case 'super_admin': return 'Super Administrateur';
      default: return role;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <BenevolCheckLogo size="md" />
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              Retour au tableau de bord
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-indigo-900">Mon Profil</CardTitle>
            <p className="text-gray-500">{getRoleName(user?.role)}</p>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {success && (
                <Alert className="bg-green-50 border-green-200">
                  <AlertDescription className="text-green-800">{success}</AlertDescription>
                </Alert>
              )}

              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Informations personnelles</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Nom complet</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Association Info (for association_admin only) */}
              {user?.role === 'association_admin' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-700 border-b pb-2">Informations de l'association</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="association_name">Nom de l'association</Label>
                      <Input
                        id="association_name"
                        value={formData.association_name}
                        onChange={(e) => setFormData({...formData, association_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="association_address">Ville (pour signature)</Label>
                      <Input
                        id="association_address"
                        value={formData.association_address}
                        onChange={(e) => setFormData({...formData, association_address: e.target.value})}
                        placeholder="Ex: Dijon"
                      />
                    </div>
                  </div>

                  {/* Logo de l'association */}
                  <div className="space-y-3 pt-2">
                    <Label>Logo de l'association</Label>
                    <div className="flex items-start gap-4">
                      {/* Aperçu du logo actuel ou nouveau */}
                      <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                        {logoPreview ? (
                          <img src={logoPreview} alt="Nouveau logo" className="w-full h-full object-contain" />
                        ) : currentLogo ? (
                          <img src={currentLogo} alt="Logo actuel" className="w-full h-full object-contain" />
                        ) : (
                          <span className="text-gray-400 text-xs text-center px-2">Aucun logo</span>
                        )}
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                          <Upload size={18} />
                          <span className="text-sm">{currentLogo || logoPreview ? 'Changer le logo' : 'Choisir un logo'}</span>
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                            onChange={handleLogoChange}
                            className="hidden"
                            data-testid="logo-input-profile"
                          />
                        </label>
                        
                        {logoFile && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleLogoUpload}
                            disabled={uploadingLogo}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {uploadingLogo ? 'Upload...' : 'Enregistrer le logo'}
                          </Button>
                        )}
                        
                        <p className="text-xs text-gray-500">
                          PNG, JPG, GIF ou WebP. Max 2 Mo.<br/>
                          Le logo apparaîtra sur vos attestations.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Password Change */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Changer le mot de passe</h3>
                <p className="text-sm text-gray-500">Laissez vide si vous ne souhaitez pas changer votre mot de passe</p>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="current_password">Mot de passe actuel</Label>
                    <div className="relative">
                      <Input
                        id="current_password"
                        type={showCurrentPassword ? "text" : "password"}
                        value={formData.current_password}
                        onChange={(e) => setFormData({...formData, current_password: e.target.value})}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                      >
                        {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="new_password">Nouveau mot de passe</Label>
                      <div className="relative">
                        <Input
                          id="new_password"
                          type={showNewPassword ? "text" : "password"}
                          value={formData.new_password}
                          onChange={(e) => setFormData({...formData, new_password: e.target.value})}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                        >
                          {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="confirm_password">Confirmer le nouveau mot de passe</Label>
                      <Input
                        id="confirm_password"
                        type="password"
                        value={formData.confirm_password}
                        onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>

            <CardFooter className="flex justify-between">
              <Button type="submit" disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
                <Save size={20} className="mr-2" />
                {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </CardFooter>
          </form>
        </Card>

        {/* Delete Account Section */}
        {user?.role !== 'super_admin' && (
          <Card className="mt-6 border-red-200">
            <CardHeader>
              <CardTitle className="text-red-600">Zone de danger</CardTitle>
            </CardHeader>
            <CardContent>
              {!showDeleteConfirm ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Supprimer mon compte</p>
                    <p className="text-sm text-gray-500">
                      Cette action est irréversible. Toutes vos données seront supprimées.
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 size={20} className="mr-2" />
                    Supprimer
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <Alert variant="destructive">
                    <AlertDescription>
                      <strong>⚠️ Attention !</strong> Cette action supprimera définitivement votre compte
                      {user?.role === 'volunteer' && ' ainsi que toutes vos attestations'}.
                    </AlertDescription>
                  </Alert>
                  
                  <div>
                    <Label>Tapez <strong>SUPPRIMER</strong> pour confirmer</Label>
                    <Input
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="SUPPRIMER"
                      className="mt-2"
                    />
                  </div>
                  
                  <div className="flex gap-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                    >
                      Annuler
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteAccount}
                      disabled={loading || deleteConfirmText !== 'SUPPRIMER'}
                    >
                      {loading ? 'Suppression...' : 'Confirmer la suppression'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ProfilePage;
